/**
 * Iter 175g — Master Dashboard consolidation.
 *
 * Reusable "Revenue donut + Orders/Signups bars" charts block. Extracted
 * from the old MasterAnalytics page so the master can see the same charts
 * inside the Overview tab without duplicating the markup or hitting the
 * `/master/analytics` endpoint twice. The standalone Analytics page is
 * gone from the sidebar but the route still mounts this same component.
 */
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { GlassCard } from '@/components/dashboard/dashboard-ui';

const PIE_COLORS = ['#8B5CF6', '#22D3EE', '#A3E635', '#F59E0B', '#EC4899'];

const ChartsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-[#13153A]/95 backdrop-blur px-4 py-3 shadow-2xl text-xs">
      <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' && p.name === 'Revenue' ? `$${p.value.toFixed(2)}` : p.value}
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsChartsBlock({ analytics }) {
  const revenueBysite = (analytics?.sites || [])
    .map(s => ({ name: s.name, value: s.sales }))
    .filter(s => s.value > 0);
  const ordersBySite = (analytics?.sites || [])
    .map(s => ({ name: s.name, orders: s.orders, signups: s.signups }));

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="analytics-charts">
      {/* Revenue by site — Donut */}
      <GlassCard glow className="p-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Breakdown</p>
        <h2 className="text-lg font-bold text-white font-syne mb-5">Revenue by Site</h2>
        {revenueBysite.length > 0 ? (
          <div className="flex items-center gap-6">
            <ResponsiveContainer width="55%" height={200}>
              <PieChart>
                <defs>
                  {PIE_COLORS.map((c, i) => (
                    <linearGradient key={i} id={`donut-${i}`} x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                      <stop offset="100%" stopColor={c} stopOpacity={0.6} />
                    </linearGradient>
                  ))}
                </defs>
                <Pie data={revenueBysite} cx="50%" cy="50%" innerRadius={48} outerRadius={84} paddingAngle={2} dataKey="value" label={false}>
                  {revenueBysite.map((_, i) => (
                    <Cell key={`cell-${i}`} fill={`url(#donut-${i % PIE_COLORS.length})`} stroke="#0E0E23" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Revenue']} contentStyle={{ backgroundColor: '#13153A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {revenueBysite.map((site, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length], boxShadow: `0 0 10px ${PIE_COLORS[i % PIE_COLORS.length]}80` }} />
                    <span className="text-[#A5A8C9] truncate max-w-[120px]">{site.name}</span>
                  </div>
                  <span className="text-[#A3E635] font-bold tabular-nums">${site.value.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-[#7B7FA8]">No revenue data yet</div>
        )}
      </GlassCard>

      {/* Orders & Signups bar chart */}
      <GlassCard glow className="p-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Activity</p>
        <h2 className="text-lg font-bold text-white font-syne mb-5">Orders & Signups by Site</h2>
        {ordersBySite.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={ordersBySite} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="bar-orders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.4} />
                </linearGradient>
                <linearGradient id="bar-signups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.4} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#7B7FA8', fontSize: 11 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#7B7FA8', fontSize: 11 }} />
              <Tooltip content={<ChartsTooltip />} cursor={{ fill: 'rgba(167,139,250,0.06)' }} />
              <Bar dataKey="orders"  fill="url(#bar-orders)"  name="Orders"  radius={[6, 6, 0, 0]} />
              <Bar dataKey="signups" fill="url(#bar-signups)" name="Signups" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-[#7B7FA8]">No data yet</div>
        )}
      </GlassCard>
    </div>
  );
}
